import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { IntelligenceTypes } from '@mono/proto-clients';
import { Label, SpecraButton, SpecraInlineBanner, SpecraInput } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Paperclip } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useUploadArtifacts } from '../../../hooks/useDeliveryDocuments';
import { DocumentsPanel } from '../../Capture/DocumentsPanel';
import { formatLocalYmd, parseLocalDate } from '../_shared/api/nullable';
import { DocumentPreviewDialog } from '../_shared/components/DocumentPreviewDialog';
import { EntityFormDialog } from '../_shared/components/EntityFormDialog';
import { EntityTable } from '../_shared/components/EntityTable';
import { IntelligenceFailureNotice } from '../_shared/components/IntelligenceFailureNotice';
import { IntelligenceReextractButton } from '../_shared/components/IntelligenceReextractButton';
import { EmptyState } from '../_shared/components/PageStates';
import { ProposalReview } from '../_shared/components/ProposalReview';
import { ScoreDetailDrawer } from '../_shared/components/ScoreDetailDrawer';
import { useProgramWriteAccess } from '../Programs/programWriteAccess';
import { useCustomerTouchpoints, useUpsertCustomerTouchpoint } from './hooks';
import { makeTouchpointFileBackend, useAttachTouchpointFile, useTouchpointFiles } from './touchpointFileHooks';
// Video's stored value stays 'Video' (the column is free text, varchar(30),
// and legacy rows already hold it) — only the label changes to "Video
// Meeting". A value change would need a data migration for every existing
// row; a label change needs neither. 'Written' is dropped from the choices
// offered for new entries (SAD-163: "written can just be rolled up into
// email"), but a legacy 'Written' record must still display and round-trip —
// see methodOptionsWithLegacy/methodLabel below.
const methodOptions = [
    {
        value: 'Email',
        label: 'Email'
    },
    {
        value: 'Phone',
        label: 'Phone'
    },
    {
        value: 'In-person',
        label: 'In-person'
    },
    {
        value: 'Video',
        label: 'Video Meeting'
    }
];
// Value → label for read display, so 'Video' shows "Video Meeting" while an
// unrecognized legacy value (e.g. 'Written') still shows its own text rather
// than going blank.
function methodLabel(value) {
    return methodOptions.find((o)=>o.value === value)?.label ?? value;
}
// The select offered when editing: methodOptions, plus the record's own
// stored value appended if it isn't already one of the choices — so editing a
// legacy 'Written' touchpoint keeps it selected (and preserved) instead of an
// unrelated field edit silently clobbering it to whatever option renders first.
function methodOptionsWithLegacy(value) {
    if (!value || methodOptions.some((o)=>o.value === value)) return methodOptions;
    return [
        ...methodOptions,
        {
            value,
            label: value
        }
    ];
}
// First couple of names (or "—") so the table stays narrow while still
// showing who was there without opening the drawer.
function attendeesSummary(attendees) {
    if (attendees.length === 0) return '—';
    const preview = attendees.slice(0, 2).join(', ');
    return attendees.length > 2 ? `${preview} +${attendees.length - 2}` : preview;
}
// Plain-text, ordered attendee list — no contact/user lookup, so this makes no
// connections to anyone else (SAD-163). Parent-controlled state, the same
// pattern the provided-by/to pickers this replaces used, so it mounts
// identically in the create dialog's extraContent and the drawer's editControl.
function AttendeeEditor({ attendees, onChange }) {
    const [draft, setDraft] = useState('');
    const add = ()=>{
        const trimmed = draft.trim();
        if (!trimmed) return;
        onChange([
            ...attendees,
            trimmed
        ]);
        setDraft('');
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1.5",
        children: [
            /*#__PURE__*/ _jsx(Label, {
                htmlFor: "touchpoint-attendee-input",
                children: "Attendees"
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: "Add whoever was in the meeting — plain names, no contact lookup."
            }),
            attendees.length > 0 && /*#__PURE__*/ _jsx("ol", {
                className: "space-y-1",
                children: attendees.map((name, i)=>/*#__PURE__*/ _jsxs("li", {
                        className: "flex items-center justify-between gap-2 rounded-md border border-input px-2 py-1 text-sm",
                        "data-testid": Testids.deliveryTouchpoints.attendeeRow,
                        "data-row-index": i,
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "truncate",
                                children: name
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                type: "button",
                                size: "sm",
                                variant: "ghost",
                                "aria-label": `Remove ${name}`,
                                onClick: ()=>onChange(attendees.filter((_, j)=>j !== i)),
                                "data-testid": Testids.deliveryTouchpoints.attendeeRemove,
                                children: "Remove"
                            })
                        ]
                    }, i))
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraInput, {
                        id: "touchpoint-attendee-input",
                        value: draft,
                        onChange: (e)=>setDraft(e.target.value),
                        onKeyDown: (e)=>{
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                add();
                            }
                        },
                        placeholder: "Attendee name",
                        "data-testid": Testids.deliveryTouchpoints.attendeeInput
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        type: "button",
                        variant: "secondary",
                        onClick: add,
                        "data-testid": Testids.deliveryTouchpoints.attendeeAdd,
                        children: "Add"
                    })
                ]
            })
        ]
    });
}
// Stages one or more files for a touchpoint that doesn't have an id yet — the
// create dialog's analog of ScoreDocumentUpload.tsx (:50-53): every picked
// file uploads to tesoro immediately via useUploadArtifacts, and
// CustomerTouchpointsTab links each staged artifact to the touchpoint id the
// create call returns. If the dialog is cancelled instead of submitted, an
// already-uploaded file stays an unlinked orphan in the tenant's artifact
// store — the same accepted tradeoff ScoreDocumentUpload documents, not a new
// one introduced here.
function TouchpointFileStaging({ staged, onChange }) {
    const upload = useUploadArtifacts();
    const inputRef = useRef(null);
    const onPick = async (e)=>{
        const files = Array.from(e.target.files ?? []);
        e.target.value = '';
        if (files.length === 0) return;
        try {
            const result = await upload.mutateAsync({
                files
            });
            result.failed?.forEach((f)=>toast.error(`Failed to upload ${f.name}: ${f.error}`));
            if (result.files.length > 0) {
                onChange([
                    ...staged,
                    ...result.files.map((f)=>({
                            artifactId: f.artifactId,
                            fileName: f.name
                        }))
                ]);
            }
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Upload failed');
        }
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1.5",
        children: [
            /*#__PURE__*/ _jsx(Label, {
                htmlFor: "touchpoint-attachment-input",
                children: "Attachments"
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: "Attach any documents from the meeting — uploaded now, linked to this touchpoint on save."
            }),
            staged.length > 0 && /*#__PURE__*/ _jsx("ul", {
                className: "space-y-1",
                children: staged.map((f)=>/*#__PURE__*/ _jsxs("li", {
                        className: "flex items-center gap-2 text-sm text-muted-foreground",
                        children: [
                            /*#__PURE__*/ _jsx(Paperclip, {
                                className: "h-3.5 w-3.5"
                            }),
                            f.fileName
                        ]
                    }, f.artifactId))
            }),
            /*#__PURE__*/ _jsx("input", {
                id: "touchpoint-attachment-input",
                ref: inputRef,
                type: "file",
                multiple: true,
                className: "hidden",
                disabled: upload.isPending,
                onChange: onPick,
                "data-testid": Testids.deliveryTouchpoints.attachmentFileInput
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                type: "button",
                size: "sm",
                variant: "secondary",
                disabled: upload.isPending,
                onClick: ()=>inputRef.current?.click(),
                "data-testid": Testids.deliveryTouchpoints.attachmentUploadButton,
                children: upload.isPending ? 'Uploading…' : 'Attach files'
            })
        ]
    });
}
// The extractor gives the summary a plain string proposed_value and each
// action item a Struct { description, ownerName, dueDate } (see
// touchpoint_extractor.go's buildDocumentResult) - both bind to the SAME
// SUMMARY_AND_ACTION_ITEMS destination, so this one parse tells them apart by
// shape. ownerName/dueDate are the extractor's grounded-or-empty convention
// (never invented, see resolveActionItemOwnerAndDate) - an empty one is
// simply omitted here rather than rendered as a placeholder.
function parseSummaryOrActionItem(raw) {
    if (typeof raw === 'string') {
        return raw ? {
            kind: 'summary',
            text: raw
        } : undefined;
    }
    if (raw && typeof raw === 'object') {
        const obj = raw;
        const description = typeof obj.description === 'string' ? obj.description : '';
        if (!description) return undefined;
        const owner = typeof obj.ownerName === 'string' ? obj.ownerName : '';
        const due = typeof obj.dueDate === 'string' ? obj.dueDate : '';
        const meta = [
            owner && `Owner: ${owner}`,
            due && `Due: ${due}`
        ].filter(Boolean).join(', ');
        return {
            kind: 'actionItem',
            text: meta ? `${description} (${meta})` : description
        };
    }
    return undefined;
}
// Maps TouchpointContract's three declared destinations onto this form's RHF
// field names (see touchpoint_extractor.go's TouchpointContract). The two
// contact-id destinations (PROVIDED_BY/TO_CONTACT_ID) are deliberately absent:
// the extractor never proposes them, and this tab's contact pickers stay
// user-driven only - see CustomerTouchpointsTab's onApplyToDraft for why
// participants never populate them.
const TOUCHPOINT_FIELD_BINDINGS = {
    [IntelligenceTypes.DestinationFieldName.BRIEF_DESCRIPTION]: {
        formField: 'briefDescription',
        label: 'Description'
    },
    [IntelligenceTypes.DestinationFieldName.DATE_OF_BRIEFING]: {
        formField: 'dateOfBriefing',
        label: 'Date'
    },
    [IntelligenceTypes.DestinationFieldName.SUMMARY_AND_ACTION_ITEMS]: {
        formField: 'summaryAndActionItems',
        label: 'Summary & action items',
        parse: parseSummaryOrActionItem
    }
};
// The extractor can emit more than one summary proposal for the same
// touchpoint (e.g. two notes-tier documents disagree and both surface as
// NEEDS_USER_REVIEW - see TestTouchpointExtractor_ConflictingNotesTierDocuments_NeedsUserReview
// in touchpoint_extractor_test.go); a reviewer can accept more than one. This
// marker delimits the block this function generates so a later apply can
// find and replace it instead of blindly appending - it must appear exactly
// as "blank line, marker, newline" to be recognized, so manual text that
// merely mentions it inline (no surrounding blank line) is never mistaken
// for a block boundary.
const GENERATED_NOTES_MARKER = '--- From meeting notes ---';
const GENERATED_BLOCK_PREFIX = `\n\n${GENERATED_NOTES_MARKER}\n`;
// Removes a previously-composed generated block from the tail of `text`, so
// re-composing replaces it rather than appending another copy. Only the
// LAST occurrence of the exact delimiter shape counts, and only when it's
// the closing act of the string (which is guaranteed for anything this
// module ever wrote) - a coincidental, non-delimiter-shaped mention of the
// marker text elsewhere in the user's own notes is left untouched.
function stripGeneratedBlock(text) {
    const idx = text.lastIndexOf(GENERATED_BLOCK_PREFIX);
    return (idx === -1 ? text : text.slice(0, idx)).trim();
}
// Composes every accepted summary and action item into ONE string for the
// single `summaryAndActionItems` field, in a stable order (summaries in the
// order accepted, then action items in the order accepted) - never
// last-write-wins, since ProposalReview hands back one array entry per
// accepted proposal and several of them target this same field (see the
// design note on TouchpointContract's client_references for why summary and
// each action item are independently accept/reject-able). Whatever the user
// already typed is preserved underneath a heading rather than overwritten -
// meeting-note extraction is additive context, not a replacement for the
// user's own notes - and a repeat apply (same or changed accepted set)
// replaces the previously-generated block in place rather than piling up a
// new copy underneath it each time.
function composeSummaryAndActionItems(existing, applied) {
    if (applied.length === 0) return existing;
    const summaries = applied.filter((p)=>p.kind === 'summary').map((p)=>p.text);
    const actionItems = applied.filter((p)=>p.kind === 'actionItem');
    const lines = [
        ...summaries
    ];
    if (actionItems.length > 0) {
        lines.push('Action items:');
        for (const item of actionItems)lines.push(`- ${item.text}`);
    }
    const generated = lines.join('\n');
    if (!generated) return existing;
    const manual = stripGeneratedBlock(existing);
    return manual ? `${manual}${GENERATED_BLOCK_PREFIX}${generated}` : generated;
}
// onApplyToDraft's handler: BRIEF_DESCRIPTION/DATE_OF_BRIEFING write straight
// through (one proposal per field, same as every other ProposalReview
// consumer); every SUMMARY_AND_ACTION_ITEMS entry - the summary plus each
// accepted action item - is composed together and written once.
function applyTouchpointProposals(form, values) {
    const summaryParts = [];
    for (const { formField, value } of values){
        if (formField === 'summaryAndActionItems') {
            if (value && typeof value === 'object') summaryParts.push(value);
            continue;
        }
        form.setValue(formField, value);
    }
    if (summaryParts.length > 0) {
        const existing = String(form.getValues('summaryAndActionItems') ?? '');
        form.setValue('summaryAndActionItems', composeSummaryAndActionItems(existing, summaryParts));
    }
}
const AUDIO_MIME_PREFIX = 'audio/';
const ymd = (v)=>v ? formatLocalYmd(v) ?? '—' : '—';
// #6: the table shows Description/Date/Method/Attendees; briefing level,
// comments, summary, and attachments live in the read-only detail drawer.
const columns = [
    {
        key: 'briefDescription',
        header: 'Description'
    },
    {
        key: 'dateOfBriefing',
        header: 'Date',
        render: (row)=>ymd(row.dateOfBriefing)
    },
    {
        key: 'method',
        header: 'Method',
        render: (row)=>methodLabel(row.method) || '—'
    },
    {
        key: 'attendees',
        header: 'Attendees',
        render: (row)=>attendeesSummary(row.attendees ?? [])
    }
];
const fields = [
    // #5 brief description at the very top.
    {
        kind: 'text',
        name: 'briefDescription',
        label: 'Brief touchpoint description',
        maxLength: 300
    },
    {
        kind: 'date',
        name: 'dateOfBriefing',
        label: 'Date',
        required: true
    },
    {
        kind: 'select',
        name: 'method',
        label: 'Method',
        required: true,
        options: methodOptions
    },
    // Attendees is the AttendeeEditor in extraContent, parent-controlled state.
    {
        kind: 'textarea',
        name: 'comments',
        label: 'Comments',
        rows: 2
    },
    // #4 summary after comments (auto-filled later by doc processing; for now free text).
    {
        kind: 'textarea',
        name: 'summaryAndActionItems',
        label: 'Touchpoint summary and action items',
        rows: 3
    }
];
export function CustomerTouchpointsTab({ programId, requestCreate = false, onCreateRequestHandled }) {
    const canEdit = useProgramWriteAccess(programId).touchpoints;
    const { data, isLoading } = useCustomerTouchpoints(programId);
    const touchpointFileBackend = useMemo(()=>makeTouchpointFileBackend(programId), [
        programId
    ]);
    const upsert = useUpsertCustomerTouchpoint();
    const attachFile = useAttachTouchpointFile(programId);
    const items = data?.items ?? [];
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [detailId, setDetailId] = useState(null);
    // Derive the viewed record from `items` (store only the id) so an open
    // drawer always reflects the latest list without a re-sync effect that
    // infinite-loops when the list returns fresh-but-equal refs each render.
    const detail = detailId ? items.find((r)=>r.id === detailId) ?? null : null;
    // Whether the detail drawer opens in read or straight into edit (row Edit).
    const [detailMode, setDetailMode] = useState('read');
    // The open touchpoint's attached files, fetched directly (not through the
    // DocumentsPanel backend, which narrows away mime_type) so the review flow
    // can spot an audio attachment and source a run from every non-audio one.
    const touchpointFiles = useTouchpointFiles(programId, detail?.id ?? '').data ?? [];
    const hasAudioAttachment = touchpointFiles.some((f)=>f.mimeType.startsWith(AUDIO_MIME_PREFIX));
    const noteArtifactIds = touchpointFiles.filter((f)=>!f.mimeType.startsWith(AUDIO_MIME_PREFIX)).map((f)=>f.artifactId);
    // Attendees is a parent-controlled ordered list (like the provided-by/to
    // pickers it replaces): plain names, no id, kept out of the RHF form so the
    // create dialog and the drawer's inline edit can share one piece of state.
    const [attendees, setAttendees] = useState([]);
    // The run a re-extraction just started, so the review panel below can pin
    // to it instead of the awaiting-review list's first entry - the earlier
    // run's state doesn't change the instant a new one starts, so runs[0]
    // would otherwise still be the STALE run while the new one processes.
    // Cleared whenever a different touchpoint's drawer opens.
    const [pinnedRunId, setPinnedRunId] = useState(undefined);
    // The create dialog's staged-but-not-yet-linked attachments — cleared on
    // every openCreate and after the post-create link attempt below, so a
    // reopened dialog never carries over a previous attempt's staged list.
    const [stagedFiles, setStagedFiles] = useState([]);
    // The attachment row picked for in-app preview (Deliverable 2); reset
    // whenever a different touchpoint's drawer opens, same as pinnedRunId.
    const [previewDoc, setPreviewDoc] = useState(null);
    useEffect(()=>{
        setPinnedRunId(undefined);
        setPreviewDoc(null);
    }, [
        detailId
    ]);
    const openCreate = ()=>{
        setEditing(null);
        setAttendees([]);
        setStagedFiles([]);
        setFormOpen(true);
    };
    // REG-11: the registry Person page's "Log touchpoint" asks for the create
    // form on arrival (TouchpointsPage reads ?logTouchpoint=1). Open it once,
    // only for a writer, then tell the page to drop the params.
    // biome-ignore lint/correctness/useExhaustiveDependencies: openCreate only sets state; runs when the request or write access changes
    useEffect(()=>{
        if (!requestCreate || !canEdit) return;
        openCreate();
        onCreateRequestHandled?.();
    }, [
        requestCreate,
        canEdit,
        onCreateRequestHandled
    ]);
    // Shared by the create modal and the drawer edit-in-place; the target id is
    // passed in (empty = create) since the two surfaces track different records
    // (modal → `editing`, drawer → `detail`). briefingLevel and the four
    // provided-by/to fields are no longer sent: the handler ignores them on
    // both create and update, so stopping here is safe (see hooks.ts). Returns
    // the upserted record so the create path can read the id the server
    // assigned — needed to link any staged attachments below.
    const submitTouchpoint = (values, id)=>{
        return upsert.mutateAsync({
            id,
            programId,
            briefDescription: values.briefDescription ?? '',
            method: values.method,
            dateOfBriefing: parseLocalDate(values.dateOfBriefing),
            comments: values.comments ?? '',
            summaryAndActionItems: values.summaryAndActionItems ?? '',
            attendees
        });
    };
    // Runs only after a successful create (never on edit — the drawer's
    // DocumentsPanel already has a real touchpoint id to attach through). Each
    // staged artifact is already uploaded; only the link RPC can still fail
    // here, and a failure must never look like a silent success — see the
    // acceptance criteria on the lifecycle this satisfies. Never guesses an id:
    // an upsert response with no id links nothing and fails loudly instead.
    const linkStagedFiles = async (created)=>{
        if (stagedFiles.length === 0) return;
        if (!created?.id) {
            toast.error('Touchpoint saved, but the server returned no id — attachments were not linked. Attach them from the touchpoint’s Details tab instead.');
            setStagedFiles([]);
            return;
        }
        const touchpointId = created.id;
        const results = await Promise.allSettled(stagedFiles.map((f)=>attachFile.mutateAsync({
                touchpointId,
                artifactId: f.artifactId
            })));
        results.forEach((r, i)=>{
            if (r.status === 'rejected') {
                const message = r.reason instanceof Error ? r.reason.message : String(r.reason);
                toast.error(`Touchpoint saved, but "${stagedFiles[i].fileName}" could not be attached: ${message}`);
            }
        });
        setStagedFiles([]);
    };
    // The attendee editor — shared verbatim by the create modal (in extraContent)
    // and the drawer's inline edit (dropped into its Attendees row via
    // editControl). Both drive the same parent state.
    const attendeeEditor = /*#__PURE__*/ _jsx(AttendeeEditor, {
        attendees: attendees,
        onChange: setAttendees
    });
    // Create-dialog-only extraContent: attendees plus the file-staging editor.
    // The drawer's inline edit uses attendeeEditor alone (via editControl on its
    // Attendees row) — a real touchpoint id already exists there, so new files
    // attach through the drawer's own DocumentsPanel instead of staging.
    const createExtraContent = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            attendeeEditor,
            /*#__PURE__*/ _jsx(TouchpointFileStaging, {
                staged: stagedFiles,
                onChange: setStagedFiles
            })
        ]
    });
    // The drawer's inline-edit config, bound to the record being viewed. The
    // editable controls live on the rows (below); this carries only the form-level
    // concerns. onEditStart seeds the out-of-form attendee list from the record.
    const detailEdit = detail ? {
        defaultValues: {
            briefDescription: detail.briefDescription ?? '',
            method: detail.method ?? '',
            dateOfBriefing: formatLocalYmd(detail.dateOfBriefing) ?? '',
            comments: detail.comments ?? '',
            summaryAndActionItems: detail.summaryAndActionItems ?? ''
        },
        onSubmit: async (v)=>{
            await submitTouchpoint(v, detail.id);
        },
        isSubmitting: upsert.isPending,
        submitLabel: 'Save changes',
        onEditStart: ()=>setAttendees(detail.attendees ?? [])
    } : undefined;
    // Detail sections: read values plus, per row, the inline-edit control. Standard
    // fields carry an `editField` (bound to the drawer's form); attendees carries
    // an `editControl` (the parent-controlled editor). Briefing level and the
    // legacy provided-by/to values render read-only, and only when the stored
    // record actually has one — never an editField, so a historical value stays
    // legible without looking like part of the current form.
    const detailSections = detail ? [
        {
            heading: 'Touchpoint',
            rows: [
                {
                    label: 'Description',
                    value: detail.briefDescription || '—',
                    editField: {
                        kind: 'text',
                        name: 'briefDescription',
                        label: 'Brief touchpoint description',
                        maxLength: 300
                    }
                },
                {
                    label: 'Date',
                    value: ymd(detail.dateOfBriefing),
                    editField: {
                        kind: 'date',
                        name: 'dateOfBriefing',
                        label: 'Date',
                        required: true
                    }
                },
                {
                    label: 'Method',
                    value: methodLabel(detail.method) || '—',
                    editField: {
                        kind: 'select',
                        name: 'method',
                        label: 'Method',
                        required: true,
                        options: methodOptionsWithLegacy(detail.method)
                    }
                },
                ...detail.briefingLevel ? [
                    {
                        label: 'Briefing level',
                        value: detail.briefingLevel
                    }
                ] : []
            ]
        },
        {
            heading: 'Attendees',
            rows: [
                {
                    label: 'Attendees',
                    value: (detail.attendees ?? []).length > 0 ? /*#__PURE__*/ _jsx("ol", {
                        className: "list-inside list-decimal space-y-0.5",
                        children: (detail.attendees ?? []).map((name, i)=>/*#__PURE__*/ _jsx("li", {
                                children: name
                            }, i))
                    }) : '—',
                    editControl: attendeeEditor
                },
                ...detail.providedBy ? [
                    {
                        label: 'Provided by',
                        value: detail.providedBy
                    }
                ] : [],
                ...detail.providedToPrimary ? [
                    {
                        label: 'Provided to',
                        value: detail.providedToPrimary
                    }
                ] : []
            ]
        },
        {
            heading: 'Notes',
            rows: [
                {
                    label: 'Comments',
                    value: detail.comments || '—',
                    editField: {
                        kind: 'textarea',
                        name: 'comments',
                        label: 'Comments',
                        rows: 2
                    }
                },
                {
                    label: 'Summary and action items',
                    value: detail.summaryAndActionItems || '—',
                    editField: {
                        kind: 'textarea',
                        name: 'summaryAndActionItems',
                        label: 'Touchpoint summary and action items',
                        rows: 3
                    }
                }
            ]
        }
    ] : [];
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "flex justify-end",
                children: /*#__PURE__*/ _jsx(SpecraButton, {
                    onClick: openCreate,
                    disabled: !canEdit,
                    "data-testid": Testids.deliveryTouchpoints.create,
                    children: "Record Touchpoint"
                })
            }),
            /*#__PURE__*/ _jsx(EntityTable, {
                data: items,
                columns: columns,
                testid: Testids.deliveryTouchpoints.table,
                rowTestid: Testids.deliveryTouchpoints.row,
                rowKey: (t)=>t.id,
                isLoading: isLoading,
                hideRowActions: !canEdit,
                onRowClick: (row)=>{
                    setDetailMode('read');
                    setDetailId(row.id);
                },
                emptyState: /*#__PURE__*/ _jsx(EmptyState, {
                    title: "No touchpoints yet",
                    description: "Touchpoints is an internal, optional log of program conversations — record one whenever there's a meeting worth keeping on file.",
                    action: !canEdit ? undefined : /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: openCreate,
                        children: "Record Touchpoint"
                    })
                }),
                rowActions: (row)=>/*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2 justify-end",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                onClick: ()=>{
                                    setDetailMode('read');
                                    setDetailId(row.id);
                                },
                                "data-testid": Testids.deliveryTouchpoints.details,
                                children: "Details"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                // Edit-in-place: open the drawer straight into edit mode; the
                                // drawer's onEditStart seeds the attendee list from this record.
                                onClick: ()=>{
                                    setDetailMode('edit');
                                    setDetailId(row.id);
                                },
                                "data-testid": Testids.deliveryTouchpoints.edit,
                                children: "Edit"
                            })
                        ]
                    })
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: formOpen,
                onOpenChange: setFormOpen,
                readOnly: !canEdit,
                title: editing ? 'Edit Touchpoint' : 'Record Touchpoint',
                fields: fields,
                defaultValues: {
                    briefDescription: editing?.briefDescription ?? '',
                    method: editing?.method ?? '',
                    dateOfBriefing: formatLocalYmd(editing?.dateOfBriefing) ?? '',
                    comments: editing?.comments ?? '',
                    summaryAndActionItems: editing?.summaryAndActionItems ?? ''
                },
                extraContent: createExtraContent,
                onSubmit: async (v)=>{
                    const created = await submitTouchpoint(v, editing?.id ?? '');
                    // Only a fresh create has anything staged to link — the drawer's
                    // inline edit never populates stagedFiles (see createExtraContent).
                    if (!editing) {
                        // Captured before linkStagedFiles clears stagedFiles below.
                        const hadStagedFiles = stagedFiles.length > 0;
                        await linkStagedFiles(created);
                        // SAD-177: notes staged during create are invisible to this tab
                        // until useTouchpointFiles re-fetches for the new record's id —
                        // jump straight to its now-linked drawer instead of leaving the
                        // user to find the row and reopen it themselves to reach the
                        // (now read-mode-reachable) processing affordance.
                        if (hadStagedFiles && created?.id) {
                            setDetailMode('read');
                            setDetailId(created.id);
                        }
                    }
                    setFormOpen(false);
                },
                isSubmitting: upsert.isPending,
                submitLabel: editing ? 'Save' : 'Record'
            }),
            /*#__PURE__*/ _jsx(ScoreDetailDrawer, {
                open: !!detail,
                onOpenChange: (open)=>!open && setDetailId(null),
                title: "Touchpoint",
                sections: detailSections,
                testid: Testids.deliveryTouchpoints.detailDrawer,
                edit: detailEdit,
                canEdit: canEdit,
                initialMode: detailMode,
                // SAD-863: attaching meeting notes and processing them is where the
                // user starts, so it sits above the record's fields.
                childrenPlacement: "top",
                children: (form)=>{
                    if (!detail || !canEdit) return null;
                    return /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-3 border-b pb-4",
                        "data-testid": Testids.deliveryTouchpoints.extractTop,
                        children: [
                            /*#__PURE__*/ _jsx(DocumentsPanel, {
                                entityType: "customer_touchpoint",
                                entityId: detail.id,
                                title: "Attached files",
                                backend: touchpointFileBackend,
                                // Deliverable 2: open the shared in-app preview instead of
                                // DocumentsPanel's default new-tab openArtifact.
                                onOpenDocument: (doc)=>setPreviewDoc({
                                        artifactId: doc.artifactId,
                                        filename: doc.filename
                                    })
                            }),
                            hasAudioAttachment && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                                tone: "info",
                                "data-testid": Testids.deliveryTouchpoints.audioNotice,
                                children: "Audio transcription coming soon — this attachment won't be processed. Attach written meeting notes to extract a summary and action items."
                            }),
                            /*#__PURE__*/ _jsx(IntelligenceReextractButton, {
                                workflowKind: IntelligenceTypes.WorkflowKind.TOUCHPOINT,
                                target: {
                                    programId,
                                    entityType: 'CustomerTouchpoint',
                                    entityId: detail.id
                                },
                                sourceArtifactIds: noteArtifactIds,
                                canEdit: canEdit,
                                label: "Process attached notes",
                                description: "Extracts a summary and action items from the attached meeting notes for you to review below.",
                                testId: Testids.deliveryTouchpoints.processNotes,
                                onStarted: (run)=>{
                                    // '' means "started, but no run id yet" - ProposalReview
                                    // renders no panel for it and never falls back to runs[0].
                                    setPinnedRunId(run?.id ?? '');
                                    // Only when NOT already editing: form.startEdit() resets the
                                    // form from defaultValues (see the comment below), which would
                                    // blow away an in-progress edit if this run was started while
                                    // already reviewing a previous one.
                                    if (!form.editing) form.startEdit();
                                }
                            }),
                            form.editing && /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-3",
                                children: [
                                    /*#__PURE__*/ _jsx(IntelligenceFailureNotice, {
                                        target: {
                                            programId,
                                            entityType: 'CustomerTouchpoint',
                                            entityId: detail.id
                                        },
                                        workflowKind: IntelligenceTypes.WorkflowKind.TOUCHPOINT,
                                        pinnedRunId: pinnedRunId
                                    }),
                                    /*#__PURE__*/ _jsx(ProposalReview, {
                                        target: {
                                            programId,
                                            entityType: 'CustomerTouchpoint',
                                            entityId: detail.id
                                        },
                                        workflowKind: IntelligenceTypes.WorkflowKind.TOUCHPOINT,
                                        draftId: detail.id,
                                        fieldBindings: TOUCHPOINT_FIELD_BINDINGS,
                                        onApplyToDraft: (values)=>applyTouchpointProposals(form, values),
                                        pinnedRunId: pinnedRunId
                                    })
                                ]
                            })
                        ]
                    });
                }
            }),
            previewDoc && /*#__PURE__*/ _jsx(DocumentPreviewDialog, {
                artifactId: previewDoc.artifactId,
                title: previewDoc.filename,
                open: true,
                onOpenChange: (open)=>{
                    if (!open) setPreviewDoc(null);
                }
            })
        ]
    });
}
